// Live Flow admin map: places the "Kop"/"Staart" markers on the existing route line
// at a given along-route distance. Mirrors the exact cumulative-haversine approach
// companion-app/src/lib/geo.js already uses (haversineMeters/cumulativeDistances) —
// not a new geometry technique, just the admin-side equivalent, since this project
// deliberately doesn't share code between the two separate React apps.
const EARTH_RADIUS_M = 6371000;

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

function haversineMeters([lon1, lat1], [lon2, lat2]) {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Interpolates the [lon, lat] point on a LineString's coordinates at `targetMeters`
 * along its length. Clamps to the first/last coordinate when `targetMeters` is
 * outside [0, total length] rather than throwing — a head/tail distance derived from
 * live-flow data can legitimately land exactly at either end, or a hair past it due
 * to independent rounding between the two computations.
 *
 * Returns null when `coordinates` doesn't describe a usable line (fewer than 2
 * points), so callers can skip rendering a marker instead of crashing the map.
 */
export function pointAtDistanceAlongLine(coordinates, targetMeters) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  if (!Number.isFinite(targetMeters)) return null;
  if (targetMeters <= 0) return coordinates[0];

  let travelled = 0;
  for (let i = 0; i < coordinates.length - 1; i++) {
    const a = coordinates[i];
    const b = coordinates[i + 1];
    const segmentLength = haversineMeters(a, b);
    if (segmentLength <= 0) continue;
    if (travelled + segmentLength >= targetMeters) {
      const ratio = (targetMeters - travelled) / segmentLength;
      return [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio];
    }
    travelled += segmentLength;
  }
  return coordinates[coordinates.length - 1];
}
